// Angular
import {
  Component,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// Third-party
import { noop, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Node, NodeBodyUpdate } from '@alfresco/js-api';

// Alfresco
import {
  AppConfigService,
  CardViewBaseItemModel,
  NotificationService,
  TranslationService,
  UpdateNotification,
} from '@alfresco/adf-core';
import {
  CardViewContentUpdateService,
  CategoryService,
  ContentMetadataComponent,
  ContentMetadataService,
  ContentService,
  NodesApiService,
  TagService,
} from '@alfresco/adf-content-services';

// Project
import { InsertMediaService } from '../../../services/insert-media.service';
import { CoreContentService } from '../../../services/core-content.service';
import { WkAlfrescoApiService } from '../../../services/wk-alfresco-api.service';
import { CoreNodeService } from '../../../services/core-node.service';
import { CardViewGroup } from '@alfresco/adf-content-services/lib/content-metadata/interfaces/card-view-group.interface';
import { WkInfodrawerService } from '../services/wk-info-drawer.service';
import { WkEditorService } from '../../../services/wk-editor.service';
import { CommonConstants } from '../../../shared/common.constant';

@Component({
  selector: 'wk-content-metadata',
  templateUrl: './wk-content-metadata.component.html',
  styleUrls: ['./wk-content-metadata.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'adf-content-metadata' },
})
export class WkContentMetadataComponent
  extends ContentMetadataComponent
  implements OnInit, OnChanges, OnDestroy
{
  public wkTargetProperty: CardViewBaseItemModel;
  public mediaExpandProperties = false;
  public mediaType = '';
  public businessPropertiesChanges: boolean;
  public editable = true;
  public isCompareMode = false;
  protected onDestroy$ = new Subject<boolean>();

  @Input()
  set isEditModeInput(value: boolean) {
    this.editable = value;
  }

  get isEditModeInput(): boolean {
    return this.editable;
  }
  changedProperties: NodeBodyUpdate = {};

  private readonly coreContentService: CoreContentService;
  private readonly apiService: WkAlfrescoApiService;
  private readonly coreNodeService: CoreNodeService;
  private readonly router: Router;
  private readonly route: ActivatedRoute;
  private readonly inserMediaService: InsertMediaService;
  private readonly wkInfodrawerService: WkInfodrawerService;
  private readonly wkEditorService: WkEditorService;

  constructor(injector: Injector) {
    super(
      injector.get(ContentMetadataService),
      injector.get(CardViewContentUpdateService),
      injector.get(NodesApiService),
      injector.get(TranslationService),
      injector.get(AppConfigService),
      injector.get(TagService),
      injector.get(CategoryService),
      injector.get<ContentService>(ContentService),
      injector.get<NotificationService>(NotificationService)
    );
    this.route = injector.get<ActivatedRoute>(ActivatedRoute);
    this.router = injector.get<Router>(Router);
    this.inserMediaService =
      injector.get<InsertMediaService>(InsertMediaService);
    this.coreContentService =
      injector.get<CoreContentService>(CoreContentService);
    this.apiService = injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this.coreNodeService = injector.get<CoreNodeService>(CoreNodeService);
    this.wkInfodrawerService =
      injector.get<WkInfodrawerService>(WkInfodrawerService);
    this.isCompareMode = this.isCompareModeActive();
    this.editable = this.isEditMode() && !this.isCompareMode;
    this.wkEditorService = injector.get<WkEditorService>(WkEditorService);
  }

  ngOnInit(): void {
    this.wkInfodrawerService.metadataAction
      .pipe(
        tap((isSave: boolean) => {
          if (isSave) {
            this.saveChanges();
          }
          if (!isSave) {
            this.cancelAllChanges();
          }
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe(noop);
    this.coreContentService.cardViewUpdateInstance.itemUpdated$
      .pipe(debounceTime(500), takeUntil(this.onDestroy$))
      .subscribe((updatedNode: UpdateNotification) => {
        this.businessPropertiesChanges = !!updatedNode.changed;
        this.hasMetadataChanged = true;
        this.wkTargetProperty = updatedNode.target;
        this.updateChanges(updatedNode.changed);
      });
    this.loadMetadataProperties(this.node);

    this.inserMediaService
      .getSaveDisable()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        if (data && !this.businessPropertiesChanges) {
          this.hasMetadataChanged = false;
          this.inserMediaService.setSaveForMedia(true);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.node && !changes.node.firstChange) {
      this.loadMetadataProperties(changes.node.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  protected handleUpdateError(error: Error): void {
    return super.handleUpdateError(error);
  }

  protected loadMetadataProperties(node: Node) {
    if (node) {
      this.basicProperties$ =
        this.coreContentService.metadataInstance.getBasicProperties(node);
      this.groupedProperties$ =
        this.coreContentService.metadataInstance.getGroupedProperties(
          node,
          this.preset as string
        );
    }
  }

  public updateChanges(updatedNodeChanges: any): void {
    return super.updateChanges(updatedNodeChanges);
  }

  public saveChanges(): void {
    this.coreNodeService.nodesApiInstance
      .updateNode(this.node.id, this.changedProperties)
      .pipe(
        catchError((err) => {
          this.coreContentService.cardViewUpdateInstance.updateElement(
            this.wkTargetProperty
          );
          this.handleUpdateError(err);
          return of(null);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((updatedNode) => {
        if (updatedNode) {
          this.wkEditorService.updateTitleInDocument(
            this.changedProperties?.properties?.[CommonConstants.CM_TITLE]
          );
          this.revertChanges();
          Object.assign(this.node, updatedNode);
          if (!this.isPreviewMode()) {
            this.apiService.nodeUpdated.next(this.node);
          }
        }
        this.businessPropertiesChanges = false;
      });
    this.inserMediaService.setSaveForMedia(true);
  }

  public revertChanges(): void {
    return super.revertChanges();
  }

  public showGroup(group: CardViewGroup): boolean {
    return super.showGroup(group);
  }

  public cancelChanges(): void {
    super.revertChanges();
    this.loadMetadataProperties(this.node);
  }

  public canExpandTheCard(group: CardViewGroup): boolean {
    return group.title === this.displayAspect;
  }

  public canExpandProperties(): boolean {
    return !this.expanded || this.displayAspect === 'Properties';
  }

  public keyDown(event: KeyboardEvent): void {
    return super.keyDown(event);
  }

  cancelAllChanges() {
    this.cancelChanges();
    this.wkInfodrawerService.setCancelEvent();
  }

  expandMediaProperties(): boolean {
    this.inserMediaService.buttonEnable
      .pipe(
        filter((value) => value),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => (this.hasMetadataChanged = true));
    this.mediaType = this.inserMediaService.getMediaType();
    if (!this.editable || !this.mediaType) {
      this.inserMediaService.togglePanel = false;
      this.inserMediaService.setUpdatedImageProperties('');
      this.inserMediaService.setUpdatedMediaProperties('');
    }
    return this.inserMediaService.togglePanel;
  }

  public togglePanel(event: Event | KeyboardEvent): boolean {
    event.stopPropagation();
    this.inserMediaService.togglePanel = !this.inserMediaService.togglePanel;
    this.inserMediaService.setMediaType('');
    this.inserMediaService.setDisable(true);
    return this.inserMediaService.togglePanel;
  }

  public isPreviewMode(): boolean {
    return this.router.url.includes('preview');
  }

  public isEditMode(): boolean {
    const urlWithOutQueryParams = this.router.url.split('?')[0];
    return urlWithOutQueryParams.includes('edit');
  }

  public isCompareModeActive(): boolean {
    return this.route.parent?.snapshot.url[0].path === 'compare';
  }
}
